import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { cls } from '../../utils/helpers.js'
import { useLanguage } from '../../i18n/index.jsx'
import {
  useToastStore,
  type Toast as ToastItem,
  type ToastType,
  type ToastAction,
  type ToastOptions,
} from '../../store/toastStore.js'

const TOAST_ICONS: Record<ToastType, string> = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' }
const TOAST_DURATION: Record<ToastType, number> = { success: 3500, error: 5000, warning: 4500, info: 4000 }

export interface ToastProps {
  message?: React.ReactNode
  type?: ToastType
  action?: ToastAction
  onClose: () => void
}

export function Toast({ message, type = 'success', action, onClose }: ToastProps) {
  const { t } = useLanguage()
  useEffect(() => {
    const timer = setTimeout(onClose, TOAST_DURATION[type] || 3500)
    return () => clearTimeout(timer)
  }, [onClose, type])

  return (
    <div className={cls('toast', `toast-${type}`)} role="alert">
      <span className="toast-icon">{TOAST_ICONS[type] || '✓'}</span>
      <span className="toast-msg">{message}</span>
      {action && (
        <button className="toast-action" onClick={() => { action.onClick(); onClose() }}>
          {action.label}
        </button>
      )}
      <button onClick={onClose} className="toast-dismiss" aria-label={t('ariaDismissNotification')}>✕</button>
    </div>
  )
}

// Thin proxy onto the shared Zustand toast store. The hook exists only to
// preserve the historical `{ toasts, show, remove, addToast }` surface that
// ~40 callers across the app destructure. The actual queue lives in
// `src/store/toastStore.js`; this hook subscribes via `useToastStore` so
// every consumer re-renders when the queue changes, and writes always go
// through the store so a `show()` from any branch of the tree is visible
// to the <GlobalToastContainer /> at the root.
//
// IMPORTANT: do NOT replace the body of `show`/`remove` with `useCallback`
// or similar — callers do not memoise the returned tuple, and reading the
// fresh action reference on every render is intentional. The store
// guarantees stable action identities, so this still avoids re-renders.
export function useToast() {
  const toasts = useToastStore(state => state.toasts)
  const show = useToastStore(state => state.show)
  const dismiss = useToastStore(state => state.dismiss)

  // `remove` is the legacy name used by ~half the callers. Keep both
  // until a dedicated codemod renames every site to `dismiss`.
  const addToast = (...args: [message: string, type?: ToastType, options?: ToastOptions]) => {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
      console.warn('useToast: addToast() is deprecated, use show() instead')
    }
    return show(...args)
  }

  return { toasts, show, remove: dismiss, addToast }
}

export interface ToastContainerProps {
  toasts: ToastItem[]
  remove: (id: string) => void
}

export function ToastContainer({ toasts, remove }: ToastContainerProps) {
  if (!toasts.length) return null
  return createPortal(
    <div className="toast-container" aria-live="polite">
      {toasts.map(t => (
        <Toast key={t.id} message={t.message} type={t.type} action={t.action} onClose={() => remove(t.id)} />
      ))}
    </div>,
    document.body
  )
}
